import {
   ChangeDetectionStrategy,
   ChangeDetectorRef,
   Component,
   EventEmitter,
   Input,
   Output,
   OnChanges,
   OnDestroy,
   OnInit,
   SimpleChanges,
   ViewEncapsulation,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { MailboxFolders } from 'generated/graphql-types';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { mailboxMessageChanged } from 'src/app/signalr/currently-viewing-mailbox-message-register-hub/currently-viewing-mailbox-message-register-hub.actions';
import { AudioPlaylistPlayerService } from '../../common-components/audio-playlist-player/services/audio-playlist-player.service';
import { GetExtensionDetail_extension_mailbox_mailboxMessagesConnection } from '../extension-details/queries/generated/GetExtensionDetail';
import { MailboxMessageItemView } from './models/mailbox-message.model';
import * as MailboxMessageDetailActions from './state/mailbox-message-detail.actions';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    providers: [{ multi: true, provide: NG_VALUE_ACCESSOR, useExisting: MailboxMessagesRegisterComponent }],
    selector: 'ndt-mailbox-messages-register',
    styleUrls: ['./mailbox-messages-register.component.scss'],
    templateUrl: './mailbox-messages-register.component.html',
    standalone: false
})
export class MailboxMessagesRegisterComponent implements OnDestroy, OnChanges, OnInit {
   @Input() mailboxId: number;

   @Input() disableReset: boolean;

   @Output() unreadMessageCountChanged = new EventEmitter<number>();

   @Input() set mailboxMessages(messages: GetExtensionDetail_extension_mailbox_mailboxMessagesConnection['items']) {
      if (messages && messages.length > 0) {
         this.mailboxMessageItems = messages.map((item) => ({ ...item }));

         this.showAllMessagesChange(this.showAllMailboxMessages);
         this.applyUnreadMessagesOnlyFilter(this.showUnreadOnly);
      } else {
         // Clear everything out
         this.mailboxMessageItems = [];
         this.mailboxMessageDisplayItems = [];
         this.fileTokens = [];
         this.mailboxMessageClearDisabled = true;
         this.changeDetectorRef.markForCheck();
      }
      this.calculateFolderCounts();
      this.applyFilters();
   }
   selectedFolder: MailboxFolders = MailboxFolders.Inbox;
   MailboxFolders = MailboxFolders;
   mailboxMessageItems: MailboxMessageItemView[] = [];

   inboxCount = 0;
   friendsCount = 0;
   familyCount = 0;
   workCount = 0;

   mailboxMessageDisplayItems: MailboxMessageItemView[] = [];

   columns: string[] = ['Timestamp', 'Sender', 'isActive', 'Actions'];

   private destroyed$ = new Subject<void>();

   fileTokens: string[] = [];

   showAudioPlayer: boolean;

   currentPlaylistIndex: number;

   isPaused: boolean;

   isLoading: boolean;

   showError: boolean;

   playlistIndexSubscription: Subscription;

   isPausedSubscription: Subscription;

   isLoadingSubscription: Subscription;

   showErrorSubscription: Subscription;

   showAllMailboxMessages: boolean;

   showUnreadOnly: boolean;

   mailboxMessageClearDisabled: boolean = true;

   constructor(
      public audioService: AudioPlaylistPlayerService,
      private changeDetectorRef: ChangeDetectorRef,
      private store: Store,
      private actions$: Actions
   ) {
      this.subscribeToAudioService();
      this.showAudioPlayer = true;
   }

   ngOnInit(): void {
      if (this.mailboxId) {
         const folderStorageKey = `selectedMailboxFolder_${this.mailboxId}`;
         const savedFolder = sessionStorage.getItem(folderStorageKey);
         if (savedFolder && Object.values(MailboxFolders).includes(savedFolder as MailboxFolders)) {
            this.selectedFolder = savedFolder as MailboxFolders;
         }

         const showArchivedStorageKey = `showAllMailboxMessages_${this.mailboxId}`;
         const savedShowArchived = sessionStorage.getItem(showArchivedStorageKey);
         if (savedShowArchived !== null) {
            this.showAllMailboxMessages = savedShowArchived === 'true';
         }

         const unreadStorageKey = `showUnreadOnly_${this.mailboxId}`;
         const savedUnread = sessionStorage.getItem(unreadStorageKey);
         if (savedUnread !== null) {
            this.showUnreadOnly = savedUnread === 'true';
         }
         this.calculateFolderCounts();
         this.applyFilters();
      }
      this.actions$
         .pipe(ofType(MailboxMessageDetailActions.saveMailboxMessageDetailSuccess), takeUntil(this.destroyed$))
         .subscribe((action) => {
            if (this.mailboxMessageItems && this.currentPlaylistIndex != -1)
               this.mailboxMessageDisplayItems[this.currentPlaylistIndex].isRead = action.data.mailboxMessage.isRead;
         });
   }

   ngOnChanges({ mailboxId }: SimpleChanges) {
      if (mailboxId && !mailboxId.firstChange) {
         this.store.dispatch(mailboxMessageChanged());
      }
   }

   showAllMessagesChange(showAllAccountsSelected: boolean): void {
      this.showAllMailboxMessages = !!showAllAccountsSelected;
      const storageKey = `showAllMailboxMessages_${this.mailboxId}`;
      sessionStorage.setItem(storageKey, JSON.stringify(this.showAllMailboxMessages));

      this.calculateFolderCounts();
      this.applyFilters();
   }

   sortUnreadMessagesChange(showUnreadOnly: boolean): void {
      this.applyUnreadMessagesOnlyFilter(showUnreadOnly);
      this.unreadMessageCountChanged.emit(
         this.mailboxMessageItems.filter((message) => !message.isRead && !message.isArchived).length
      );
   }

   private applyUnreadMessagesOnlyFilter(showUnreadOnly: boolean): void {
      this.showUnreadOnly = showUnreadOnly;
      const unreadStorageKey = `showUnreadOnly_${this.mailboxId}`;
      sessionStorage.setItem(unreadStorageKey, JSON.stringify(this.showUnreadOnly));
      this.calculateFolderCounts();
      this.applyFilters();
   }

   onFolderChange(): void {
      this.applyFilters();
   }

   onMoveToFolder(message: MailboxMessageItemView, folder: MailboxFolders): void {
      const markAsRead = folder === MailboxFolders.Inbox ? false : message.isRead;
      const noChange = message.mailboxFolderId === folder && message.isRead === markAsRead;
      if (noChange) return;
      this.store.dispatch(
         MailboxMessageDetailActions.saveMailboxMessageDetail({
            mailboxMessage: {
               isRead: markAsRead,
               mailboxMessageId: message.mailboxMessageId,
               mailboxFolderId: folder,
            },
            mailboxMessageFields: ['mailboxMessageId', 'mailboxFolderId', 'isRead'],
         })
      );
      message.mailboxFolderId = folder;
      message.isRead = markAsRead;
      this.calculateFolderCounts();
      this.applyFilters();
   }

   private applyFilters(): void {
      this.mailboxMessageDisplayItems = this.mailboxMessageItems.filter((message) => {
         const isVisible = this.showAllMailboxMessages || !message.isArchived;

         if (!isVisible) {
            return false;
         }

         const matchesFolder =
            this.selectedFolder === MailboxFolders.Inbox
               ? message.mailboxFolderId === MailboxFolders.Inbox || message.mailboxFolderId === MailboxFolders.Old
               : message.mailboxFolderId === this.selectedFolder;

         return matchesFolder;
      });

      if (this.showUnreadOnly) {
         this.mailboxMessageDisplayItems = this.mailboxMessageDisplayItems.filter((message) => !message.isRead);
      }

      this.audioService.setPlaylistIndex(-1);

      if (this.mailboxId) {
         const storageKey = `selectedMailboxFolder_${this.mailboxId}`;
         sessionStorage.setItem(storageKey, this.selectedFolder);
      }

      this.fileTokens = this.mailboxMessageDisplayItems
         .map((message) => message.file?.fileToken)
         .filter((token): token is string => !!token);

      this.mailboxMessageClearDisabled =
         this.mailboxMessageDisplayItems.filter((mailbox) => !mailbox.isArchived).length < 1;

      this.changeDetectorRef.markForCheck();
   }

   private calculateFolderCounts(): void {
      this.inboxCount = 0;
      this.friendsCount = 0;
      this.familyCount = 0;
      this.workCount = 0;

      for (const message of this.mailboxMessageItems) {
         if (!this.showAllMailboxMessages && message.isArchived) {
            continue;
         }

         if (this.showUnreadOnly && message.isRead) {
            continue;
         }

         switch (message.mailboxFolderId) {
            case MailboxFolders.Inbox:
               this.inboxCount++;
               break;
            case MailboxFolders.Friends:
               this.friendsCount++;
               break;
            case MailboxFolders.Family:
               this.familyCount++;
               break;
            case MailboxFolders.Work:
               this.workCount++;
               break;
            case MailboxFolders.Old:
               this.inboxCount++;
               break;
         }
      }
   }

   subscribeToAudioService() {
      this.playlistIndexSubscription = this.audioService.currentPlaylistIndex
         .pipe(takeUntil(this.destroyed$))
         .subscribe((value) => {
            this.currentPlaylistIndex = value;
            if (this.mailboxMessageDisplayItems && this.mailboxMessageDisplayItems?.length > 0) {
               if (this.currentPlaylistIndex != -1) {
                  this.onPlayClick(this.currentPlaylistIndex);
               }
               this.actions$
                  .pipe(ofType(MailboxMessageDetailActions.saveMailboxMessageDetailSuccess), takeUntil(this.destroyed$))
                  .subscribe((action) => {
                     if (this.mailboxMessageDisplayItems[this.currentPlaylistIndex]) {
                        this.mailboxMessageDisplayItems[this.currentPlaylistIndex].isRead =
                           action.data.mailboxMessage.isRead;
                     }
                  });

               this.actions$
                  .pipe(
                     ofType(MailboxMessageDetailActions.deleteMailboxMessageDetailSuccess),
                     takeUntil(this.destroyed$)
                  )
                  .subscribe((action) => {
                     const messageArrayIndex = this.mailboxMessageDisplayItems.findIndex(
                        (m) => m.mailboxMessageId === action.mailboxMessageId
                     );
                     this.handleSuccessfulDeletion(messageArrayIndex);
                  });
            }
            this.changeDetectorRef.markForCheck();
         });
      this.isPausedSubscription = this.audioService.isPaused.pipe(takeUntil(this.destroyed$)).subscribe((value) => {
         this.isPaused = value;
         this.changeDetectorRef.markForCheck();
      });
      this.isLoadingSubscription = this.audioService.isLoading.pipe(takeUntil(this.destroyed$)).subscribe((value) => {
         this.isLoading = value;
         this.changeDetectorRef.markForCheck();
      });
      this.showErrorSubscription = this.audioService.showError.pipe(takeUntil(this.destroyed$)).subscribe((value) => {
         this.showError = value;
         this.changeDetectorRef.markForCheck();
      });
   }

   onPlayAllClick() {
      this.audioService.setPlaylistIndex(0);
      this.showAudioPlayer = true;
   }

   onStopAllClick() {
      this.showAudioPlayer = false;
   }

   onPlayClick(messageArrayIndex: number, event?: MouseEvent) {
      const message = this.mailboxMessageDisplayItems[messageArrayIndex];
      if (message.file === null) {
         return;
      }

      const playAudio = () => {
         if (!this.showAudioPlayer) {
            this.audioService.setPlaylistIndex(messageArrayIndex);
            this.showAudioPlayer = true;
         }

         if (messageArrayIndex === this.currentPlaylistIndex) {
            if (event) {
               return;
            }
            this.audioService.setIsPaused(!this.isPaused);
         } else {
            this.audioService.setPlaylistIndex(messageArrayIndex);
         }
      };

      if (message.isRead) {
         playAudio();
         return;
      }
      this.store.dispatch(
         MailboxMessageDetailActions.saveMailboxMessageDetail({
            mailboxMessage: {
               isRead: true,
               mailboxMessageId: message.mailboxMessageId,
               mailboxFolderId:
                  !message.isRead && message.mailboxFolderId === MailboxFolders.Inbox
                     ? MailboxFolders.Old
                     : message.mailboxFolderId,
            },
            mailboxMessageFields:
               !message.isRead && message.mailboxFolderId === MailboxFolders.Inbox
                  ? ['mailboxMessageId', 'isRead', 'mailboxFolderId']
                  : ['mailboxMessageId', 'isRead'],
         })
      );
      const messageSaveSubscription = this.actions$
         .pipe(ofType(MailboxMessageDetailActions.saveMailboxMessageDetailSuccess))
         .subscribe(() => {
            this.mailboxMessageDisplayItems[messageArrayIndex].isRead = true;
            playAudio();
            messageSaveSubscription.unsubscribe();
         });
   }

   onDeleteClick(messageArrayIndex: number, event?: MouseEvent) {
      if (this.mailboxMessageDisplayItems && this.mailboxMessageDisplayItems[messageArrayIndex].isArchived === false) {
         // This will trigger the effect which shows the confirmation dialog.
         this.store.dispatch(
            MailboxMessageDetailActions.deleteMailboxMessageDetail({
               mailboxMessageId: this.mailboxMessageDisplayItems[messageArrayIndex].mailboxMessageId,
            })
         );
      }
   }

   private handleSuccessfulDeletion(messageArrayIndex: number) {
      if (this.mailboxMessageDisplayItems[messageArrayIndex]) {
         this.mailboxMessageDisplayItems[messageArrayIndex].isArchived = true;
      }
      this.calculateFolderCounts();
      this.applyFilters();
   }

   onMarkUnreadClick(messageArrayIndex: number) {
      const message = this.mailboxMessageDisplayItems?.[messageArrayIndex];
      if (!message || !message.isRead) return;
      const moveToInbox =
         this.selectedFolder === MailboxFolders.Inbox || message.mailboxFolderId === MailboxFolders.Old;
      this.store.dispatch(
         MailboxMessageDetailActions.markUnreadMailboxMessage({
            mailboxMessageId: message.mailboxMessageId,
            ...(moveToInbox ? { mailboxFolderId: MailboxFolders.Inbox } : {}),
         })
      );
   }

   ngOnDestroy(): void {
      this.destroyed$.next();
   }

   getMailboxMessageIds(): number[] {
      return this.mailboxMessageItems.map((m) => m.mailboxMessageId);
   }
}
